import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { OnboardingContextValue, OnboardingState, TourDefinition } from "./types";
import { findAllToursForRoute, findTourById, findTourForRoute } from "./tours";

const LOCAL_STORAGE_KEY = "zitiu.onboarding";

interface PersistedState {
  completed: string[];
  dismissed: string[];
  autoStart: boolean;
}

const defaultState: OnboardingState = {
  completed: new Set(),
  dismissed: new Set(),
  autoStart: true,
  currentTour: null,
  loading: true,
};

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

function readLocal(): PersistedState | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return {
      completed: Array.isArray(parsed.completed) ? parsed.completed : [],
      dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed : [],
      autoStart: typeof parsed.autoStart === "boolean" ? parsed.autoStart : true,
    };
  } catch {
    return null;
  }
}

function writeLocal(state: PersistedState) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — silently ignore */
  }
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<OnboardingState>(() => {
    const local = readLocal();
    if (!local) return defaultState;
    return {
      completed: new Set(local.completed),
      dismissed: new Set(local.dismissed),
      autoStart: local.autoStart,
      currentTour: null,
      loading: true,
    };
  });

  // Avoid auto-starting the same tour twice in one session (e.g. after a
  // state mutation triggers a re-evaluation).
  const autoStartedRef = useRef<Set<string>>(new Set());

  // ---- Load from Supabase on mount / user change -------------------------
  useEffect(() => {
    if (!user) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("user_onboarding" as any)
        .select("completed_tours, dismissed_tours, prefs")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }
      const completed = new Set<string>(((data as any).completed_tours as string[]) ?? []);
      const dismissed = new Set<string>(((data as any).dismissed_tours as string[]) ?? []);
      const prefs = ((data as any).prefs as { autoStart?: boolean } | null) ?? null;
      const autoStart = prefs?.autoStart ?? true;
      setState((prev) => ({
        ...prev,
        completed,
        dismissed,
        autoStart,
        loading: false,
      }));
      writeLocal({
        completed: Array.from(completed),
        dismissed: Array.from(dismissed),
        autoStart,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ---- Persistence helper ------------------------------------------------
  const persist = useCallback(
    async (next: { completed: Set<string>; dismissed: Set<string>; autoStart: boolean }) => {
      writeLocal({
        completed: Array.from(next.completed),
        dismissed: Array.from(next.dismissed),
        autoStart: next.autoStart,
      });
      if (!user) return;
      await supabase
        .from("user_onboarding" as any)
        .upsert(
          {
            user_id: user.id,
            completed_tours: Array.from(next.completed),
            dismissed_tours: Array.from(next.dismissed),
            prefs: { autoStart: next.autoStart },
          } as any,
          { onConflict: "user_id" }
        );
    },
    [user]
  );

  // ---- Mutations ---------------------------------------------------------
  const startTour = useCallback((id: string) => {
    const tour = findTourById(id);
    if (!tour) return;
    autoStartedRef.current.add(id);
    setState((prev) => ({ ...prev, currentTour: id }));
  }, []);

  const stopTour = useCallback(() => {
    setState((prev) => ({ ...prev, currentTour: null }));
  }, []);

  const completeTour = useCallback(
    (id: string) => {
      setState((prev) => {
        const completed = new Set(prev.completed);
        completed.add(id);
        const dismissed = new Set(prev.dismissed);
        dismissed.delete(id);
        const next = { ...prev, completed, dismissed, currentTour: null };
        void persist({ completed, dismissed, autoStart: prev.autoStart });
        return next;
      });
    },
    [persist]
  );

  const dismissTour = useCallback(
    (id: string) => {
      setState((prev) => {
        const dismissed = new Set(prev.dismissed);
        dismissed.add(id);
        const next = { ...prev, dismissed, currentTour: null };
        void persist({ completed: prev.completed, dismissed, autoStart: prev.autoStart });
        return next;
      });
    },
    [persist]
  );

  const resetTour = useCallback(
    (id: string) => {
      setState((prev) => {
        const completed = new Set(prev.completed);
        completed.delete(id);
        const dismissed = new Set(prev.dismissed);
        dismissed.delete(id);
        autoStartedRef.current.delete(id);
        const next = { ...prev, completed, dismissed };
        void persist({ completed, dismissed, autoStart: prev.autoStart });
        return next;
      });
    },
    [persist]
  );

  const resetAll = useCallback(() => {
    setState((prev) => {
      autoStartedRef.current.clear();
      const next = {
        ...prev,
        completed: new Set<string>(),
        dismissed: new Set<string>(),
        currentTour: null,
      };
      void persist({
        completed: new Set(),
        dismissed: new Set(),
        autoStart: prev.autoStart,
      });
      return next;
    });
  }, [persist]);

  const setAutoStart = useCallback(
    (value: boolean) => {
      setState((prev) => {
        const next = { ...prev, autoStart: value };
        void persist({
          completed: prev.completed,
          dismissed: prev.dismissed,
          autoStart: value,
        });
        return next;
      });
    },
    [persist]
  );

  const getTourForCurrentRoute = useCallback((): TourDefinition | null => {
    return findTourForRoute(location.pathname);
  }, [location.pathname]);

  // ---- Auto-start on route change ----------------------------------------
  useEffect(() => {
    if (state.loading) return;
    if (!state.autoStart) return;
    if (state.currentTour) return;
    // Walk every tour that matches this route and pick the first one the
    // user hasn't already completed/dismissed/seen this session. Without
    // this, a "catch-all" tour like `welcome` (routeMatch /^\/app/) would
    // block page-specific tours once it's been dismissed.
    const candidates = findAllToursForRoute(location.pathname);
    const tour = candidates.find(
      (t) =>
        !state.completed.has(t.id) &&
        !state.dismissed.has(t.id) &&
        !autoStartedRef.current.has(t.id) &&
        !(t.prerequisites?.some((p) => !state.completed.has(p)))
    );
    if (!tour) return;
    // Give the page a beat to render its data-tour targets before joyride
    // starts probing for them.
    const timer = setTimeout(() => {
      autoStartedRef.current.add(tour.id);
      setState((prev) => ({ ...prev, currentTour: tour.id }));
    }, 400);
    return () => clearTimeout(timer);
  }, [location.pathname, state.loading, state.autoStart, state.currentTour, state.completed, state.dismissed]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      ...state,
      startTour,
      stopTour,
      completeTour,
      dismissTour,
      resetTour,
      resetAll,
      setAutoStart,
      getTourForCurrentRoute,
    }),
    [state, startTour, stopTour, completeTour, dismissTour, resetTour, resetAll, setAutoStart, getTourForCurrentRoute]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used inside <OnboardingProvider>");
  return ctx;
}
